import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Section, SettingRow, Toggle, SaveButton } from "./SettingsControls";
import {
    getPraxisSoftwareSyncSettings, savePraxisSoftwareSyncSettings, type PraxisSoftwareSyncSettings,
} from "src/services/agentSyncService";

const DEFAULTS: PraxisSoftwareSyncSettings = {
    autoParse: false, autoSendConsent: false, syncPaused: false,
    popups: { consent: true, invoiceSent: true, paid: true },
    documents: { consent: true, invoiceSent: false, paid: false },
};

// One row per event: filing it in the practice software, and the pop-up on this PC. The pop-up only
// comes with a filed document, so its switch is off and locked while the document switch is off.
const MATRIX_ROWS: ("consent" | "invoiceSent" | "paid")[] = ["consent", "invoiceSent", "paid"];

/** Praxis-wide policy for how synced patient/invoice data is handled — shared by the browser
 *  folder sync and the desktop agent. The live connection controls (folders, devices, activity)
 *  live on the Sync page itself (nav → Praxis Software Sync), not here — this section used to
 *  duplicate that page and has been replaced with the settings that actually belong in Settings. */
export function PraxisSoftwareSyncSettings() {
    const { t } = useTranslation();
    const [settings, setSettings] = useState<PraxisSoftwareSyncSettings>(DEFAULTS);
    const [loading, setLoading] = useState(true);
    const [dirty, setDirty] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        getPraxisSoftwareSyncSettings()
            .then((s) => { if (!cancelled) setSettings(s); })
            .catch(() => { if (!cancelled) setError(t("praxisSoftwareSyncSettings.loadError")); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [t]);

    const update = (patch: Partial<PraxisSoftwareSyncSettings>) => {
        setSettings((s) => ({ ...s, ...patch }));
        setDirty(true);
    };

    const handleSave = async () => {
        setError(null);
        try {
            const res = await savePraxisSoftwareSyncSettings(settings);
            setSettings(res.praxisSoftwareSync);
            setDirty(false);
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        } catch {
            setError(t("praxisSoftwareSyncSettings.saveError"));
        }
    };

    return (
        <Section
            title={t("praxisSoftwareSync.title")}
            description={t("praxisSoftwareSyncSettings.description")}
            actions={<SaveButton onClick={() => void handleSave()} saved={saved} dirty={dirty} />}
        >
            {loading ? (
                <p className="settings-hint">{t("common.loading")}</p>
            ) : (
                <>
                    {error && <p className="settings-error">{error}</p>}

                    <h3 className="sync-subhead">{t("praxisSoftwareSyncSettings.importTitle")}</h3>

                    <SettingRow title={t("praxisSoftwareSyncSettings.autoParseTitle")} description={t("praxisSoftwareSyncSettings.autoParseDesc")}>
                        <Toggle value={settings.autoParse} onChange={(v) => update({ autoParse: v })} ariaLabel={t("praxisSoftwareSyncSettings.autoParseTitle")} />
                    </SettingRow>

                    <SettingRow title={t("praxisSoftwareSyncSettings.autoSendConsentTitle")} description={t("praxisSoftwareSyncSettings.autoSendConsentDesc")}>
                        <Toggle value={settings.autoSendConsent} onChange={(v) => update({ autoSendConsent: v })} ariaLabel={t("praxisSoftwareSyncSettings.autoSendConsentTitle")} />
                    </SettingRow>

                    <h3 className="sync-subhead">{t("praxisSoftwareSyncSettings.matrixTitle")}</h3>
                    <p className="sync-matrix__intro">{t("praxisSoftwareSyncSettings.matrixDesc")}</p>

                    <table className="sync-matrix">
                        <thead>
                            <tr>
                                <th scope="col">{t("praxisSoftwareSyncSettings.colEvent")}</th>
                                <th scope="col" className="sync-matrix__col">{t("praxisSoftwareSyncSettings.colPractice")}</th>
                                <th scope="col" className="sync-matrix__col">{t("praxisSoftwareSyncSettings.colPopup")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {MATRIX_ROWS.map((key) => (
                                <tr key={key}>
                                    <th scope="row" className="sync-matrix__event">
                                        <span className="sync-matrix__event-title">{t(`praxisSoftwareSyncSettings.${key}Title`)}</span>
                                        <span className="sync-matrix__event-hint">{t(`praxisSoftwareSyncSettings.${key}Hint`)}</span>
                                    </th>
                                    <td>
                                        <Toggle
                                            value={settings.documents[key]}
                                            onChange={(v) => update({ documents: { ...settings.documents, [key]: v } })}
                                            ariaLabel={`${t(`praxisSoftwareSyncSettings.${key}Title`)} — ${t("praxisSoftwareSyncSettings.colPractice")}`}
                                        />
                                    </td>
                                    <td>
                                        <Toggle
                                            value={settings.documents[key] && settings.popups[key]}
                                            onChange={(v) => update({ popups: { ...settings.popups, [key]: v } })}
                                            disabled={!settings.documents[key]}
                                            ariaLabel={`${t(`praxisSoftwareSyncSettings.${key}Title`)} — ${t("praxisSoftwareSyncSettings.colPopup")}`}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <SettingRow title={t("praxisSoftwareSyncSettings.syncPausedTitle")} description={t("praxisSoftwareSyncSettings.syncPausedDesc")} className="setting-row--danger">
                        <Toggle value={settings.syncPaused} onChange={(v) => update({ syncPaused: v })} ariaLabel={t("praxisSoftwareSyncSettings.syncPausedTitle")} />
                    </SettingRow>
                </>
            )}
        </Section>
    );
}
